import { forwardRef } from "react";
import { useTranslationApp } from "../../lib/i18next";
import {
  BULK_CAMPAIGNS_ALERT_ANALYSIS_ERROR,
  BULK_CAMPAIGNS_ALERT_ANALYSIS_SUCCESS,
  BULK_CAMPAIGNS_ALERT_ANALYZING,
  BULK_CAMPAIGNS_ALERT_GENERATING,
  BULK_CAMPAIGNS_ALERT_GENERATION_ERROR,
  BULK_CAMPAIGNS_ALERT_GENERATION_SUCCESS,
} from "../../i18n/keysTranslations";
import { ALERTS_ASYNC_STATUS } from "../../utils/constants";
import { openAlertModal } from "../../utils/alertModalsState";
import { BULK_CAMPAIGNS_JOB_KIND } from "../../actions/bulkCampaignsJobs";
import { useCreativeJobProgressLoader } from "../../hooks/useCreativeJobProgressLoader";
import {
  BULK_CAMPAIGNS_ANALYSIS_TASK,
  BULK_CAMPAIGNS_GENERATION_TASK,
} from "../../utils/taskEstimatedDurations";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import TaskAlertCard from "./TaskAlertCard";
import TaskAlertDiscardActions from "./TaskAlertDiscardActions";

const MESSAGE_KEYS = {
  [BULK_CAMPAIGNS_JOB_KIND.analysis]: {
    loading: BULK_CAMPAIGNS_ALERT_ANALYZING,
    success: BULK_CAMPAIGNS_ALERT_ANALYSIS_SUCCESS,
    error: BULK_CAMPAIGNS_ALERT_ANALYSIS_ERROR,
  },
  [BULK_CAMPAIGNS_JOB_KIND.generation]: {
    loading: BULK_CAMPAIGNS_ALERT_GENERATING,
    success: BULK_CAMPAIGNS_ALERT_GENERATION_SUCCESS,
    error: BULK_CAMPAIGNS_ALERT_GENERATION_ERROR,
  },
};

const BulkCampaignsAlertItem = forwardRef(({ alert, onClose }, ref) => {
  const { t } = useTranslationApp();
  const { id, status, generationData } = alert;

  const kind = generationData?.kind || BULK_CAMPAIGNS_JOB_KIND.analysis;
  const isGeneration = kind === BULK_CAMPAIGNS_JOB_KIND.generation;
  const isError = status === ALERTS_ASYNC_STATUS.ERROR;
  const isSuccess = status === ALERTS_ASYNC_STATUS.SUCCESS;
  const isLoading = !isError && !isSuccess;
  const keys = MESSAGE_KEYS[kind];
  const count =
    generationData?.campaigns?.length || generationData?.campaignCount || 0;

  const messageKey = isError
    ? keys.error
    : isSuccess
      ? keys.success
      : keys.loading;

  const onView = () => {
    if (isLoading) return;
    openAlertModal(id, generationData, () => {});
  };

  const {
    loaderTimeMs,
    onProgressChange,
    seedProgress,
    formattedRemainingTime,
  } = useCreativeJobProgressLoader({
    storageKey: id,
    jobType: isGeneration
      ? BULK_CAMPAIGNS_GENERATION_TASK
      : BULK_CAMPAIGNS_ANALYSIS_TASK,
    itemCount: count,
    isTerminal: !isLoading,
  });

  return (
    <TaskAlertCard
      ref={ref}
      estimatedTime={isLoading ? formattedRemainingTime : null}
      status={isError ? "error" : isSuccess ? "success" : "inProgress"}
      label={t(messageKey, { count })}
      progress={isLoading ? seedProgress : 100}
      showProgressBar={!isLoading}
      showProgressPercent={!isLoading}
      progressSlot={
        isLoading ? (
          <LinearLoaderWithTime
            isAlert={true}
            labelSide="bottom"
            time={loaderTimeMs}
            progress={seedProgress}
            autoIncrement={true}
            max={99}
            onProgressChange={onProgressChange}
          />
        ) : null
      }
      onClick={isLoading ? undefined : onView}
      cursor={isLoading ? "default" : "pointer"}
      actions={
        isLoading ? null : (
          <TaskAlertDiscardActions
            onStartDiscard={() => onClose(id)}
            onView={onView}
          />
        )
      }
    />
  );
});

BulkCampaignsAlertItem.displayName = "BulkCampaignsAlertItem";

export default BulkCampaignsAlertItem;
